/**
 * publicLanding.js — the one list of "pages an unauthenticated stranger reaches by
 * clicking a link in an email".
 *
 * WHY THIS EXISTS
 * These landings are structurally identical: the recipient is UNAUTH by definition,
 * the link carries its credential in the REAL query string (mvvLegit strips the hash
 * tail, so params cannot ride there), and the page is hosted on `#/splash` because
 * splash is the only route mvvLegit permits in every auth state — including state 0,
 * where a cold load begins.
 *
 * Two places have to agree about them:
 *   1. main.js's loadSplash(), which decides WHICH module to render.
 *   2. AuthController's mvvLegit nav guard, which has to SUPPRESS the automatic
 *      "unauthenticated users go to signin" redirect that fires a beat later, once
 *      mvvLegit's init resolves and the state settles to UNAUTH.
 *
 * Getting (2) wrong does not look like a routing bug. The page renders correctly and
 * is then replaced by the sign-in screen a second or two later, which reads like a
 * timer or a failed fetch rather than a redirect.
 *
 * That has now gone wrong twice, both times as drift between those two places:
 *   - First the guard tested the HASH for '#/accept-invite' — a route never actually
 *     navigated to, since invites render at #/splash — so it never fired at all.
 *     Fixed by sharing isInviteLink() with loadSplash.
 *   - Then the questionnaire landing was added to loadSplash and the guard, which
 *     enumerated invite specifically, silently did not cover it. Every questionnaire
 *     recipient was bounced to signin about two seconds after the form appeared.
 *
 * The recurring fault is the guard asking WHICH landing this is instead of WHETHER it
 * is one. So the answer lives here, once, and both callers ask this module.
 */

import {signinLanding} from '../services/shopify/signin-flow.js';
import { isInviteLink } from './inviteLink.js';
import { isQuestionnaireLink } from './questionnaireLink.js';
import { isDeliverableLink } from './deliverableLink.js';

/**
 * Every public token-carrying landing, in precedence order — `find` returns the first
 * match, which preserves loadSplash's original "invite wins if both params somehow
 * appear" behaviour rather than leaving it ambiguous.
 *
 * `name` is what loadSplash branches on. The module is deliberately NOT stored here:
 * Vite only emits a chunk for a LITERAL import() string, so the actual imports have to
 * stay inline at the call site. A test asserts every name here has a branch there.
 */
export const PUBLIC_TOKEN_LANDINGS = Object.freeze([
  Object.freeze({ name: 'invite', matches: isInviteLink }),
  Object.freeze({ name: 'questionnaire', matches: isQuestionnaireLink }),
  Object.freeze({ name: 'deliverable', matches: isDeliverableLink }),
  Object.freeze({ name: 'shopifySignin', matches: signinLanding }),
]);

/**
 * Which public landing (if any) the current URL is.
 * @param {string} [search] defaults to window.location.search
 * @returns {{name: string, matches: Function}|null}
 */
export function publicLandingFor(search = window.location.search) {
  return PUBLIC_TOKEN_LANDINGS.find((landing) => landing.matches(search)) || null;
}

/**
 * Is this URL any public token-carrying landing?
 *
 * This is the question the auth guard should ask. Anything that answers true here is a
 * page an unauthenticated visitor is SUPPOSED to be on, so mvvLegit's redirect to
 * signin/splash must be suppressed while they are on it.
 *
 * @param {string} [search] defaults to window.location.search
 * @returns {boolean}
 */
export function isPublicTokenLanding(search = window.location.search) {
  return publicLandingFor(search) !== null;
}

/**
 * Hash routes an unauthenticated stranger may browse with NO token at all —
 * the second kind of public page. First (and so far only) member: #/templates,
 * the Template Library gallery. It renders from the public /templates catalog
 * and holds no tenant data, and the website links straight into it; mvvLegit's
 * "unauth → signin" redirect would bounce those visitors off the page they
 * followed a link to, exactly like the token landings above.
 *
 * Prefix-matched (a '#/templates/...' child route stays public); everything
 * else keeps the normal redirect.
 *
 * #/signup and #/forgot joined 2026-08-26: the pricing page's "Start free"
 * CTAs deep-link to #/signup, and a COLD load starts in mvvLegit state 0,
 * where signup_route is not yet allowed — the page rendered, then init
 * settled to UNAUTH and the automatic redirect replaced it with signin.
 * Same failure class this file exists for; same cure.
 */
export const PUBLIC_HASH_ROUTES = Object.freeze(['#/templates', '#/signup', '#/forgot']);

/**
 * Is the current URL's hash one of the public browse routes?
 * @param {string} [hash] defaults to window.location.hash
 * @returns {boolean}
 */
export function isPublicHashRoute(hash = window.location.hash) {
  return PUBLIC_HASH_ROUTES.some((h) => hash === h || hash.startsWith(h + '/'));
}

/**
 * Should mvvLegit's navigation callback be ignored for this redirect?
 *
 * The whole guard, as one pure function, so the decision can be tested directly
 * instead of by reading AuthController's source. On a public token landing, EVERY
 * mvvLegit-initiated redirect is suppressed — the page is valid for any auth state:
 *
 *   - unauthenticated recipient: mvvLegit's "go to splash/signin" would bounce them
 *     off the page they were emailed a link to (guard v2/v3 history above).
 *   - authenticated viewer (a reseller previewing the link they just copied, or a
 *     client who happens to have an account): mvvLegit's "authed users don't belong
 *     on splash, go HOME" bounced them to the dashboard ~2s after the form painted —
 *     guard failure v4, found live 2026-08-12.
 *
 * v1-v4 of this guard's history say the same thing: inspecting the DESTINATION is
 * how it keeps dying (v3 died comparing the raw 'signin_route' token against
 * 'signin'; that trap is gone with the comparison itself). The one flow the old
 * home-exception protected — an invitee who just accepted + doSignin'd — does not
 * need the callback at all: doSignin() navigates home EXPLICITLY (its own comment
 * says "don't rely on callback").
 *
 * @param {string} _routeName the route mvvLegit wants to send the visitor to —
 *   accepted for call-site compatibility, deliberately not consulted.
 * @param {string} [search] defaults to window.location.search
 * @returns {boolean} true = stay put
 */
export function shouldSuppressAuthRedirect(_routeName, search = window.location.search, hash = window.location.hash) {
  return isPublicTokenLanding(search) || isPublicHashRoute(hash);
}

export default { PUBLIC_TOKEN_LANDINGS, PUBLIC_HASH_ROUTES, publicLandingFor, isPublicTokenLanding, isPublicHashRoute, shouldSuppressAuthRedirect };
